import React from 'react';
import { useAuth } from '../context/AuthContext';
import { Award, Lock, Star } from 'lucide-react';
import { motion } from 'framer-motion';
import confetti from 'canvas-confetti';

const Medal = ({ unlocked, title, description, score, delay }) => {
    return (
        <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay, duration: 0.5, type: 'spring' }}
            className={`relative p-6 rounded-3xl border-4 flex flex-col items-center text-center ${unlocked
                ? 'bg-yellow-50 border-brand-yellow shadow-xl'
                : 'bg-gray-50 border-gray-200 opacity-70 grayscale'
                }`}
        >
            <div className={`w-24 h-24 mb-4 rounded-full flex items-center justify-center border-4 ${unlocked ? 'bg-brand-yellow border-orange-300' : 'bg-gray-200 border-gray-300'
                }`}>
                {unlocked ? (
                    <Award size={48} className="text-white drop-shadow-md" />
                ) : (
                    <Lock size={40} className="text-gray-400" />
                )}
            </div>

            <h3 className={`text-xl font-bold mb-1 ${unlocked ? 'text-gray-800' : 'text-gray-500'}`}>{title}</h3>
            {unlocked ? (
                <>
                    <p className="text-brand-orange font-bold text-lg mb-2">¡Completado!</p>
                    <div className="bg-white px-4 py-1 rounded-full shadow-sm border border-gray-100 flex items-center gap-2">
                        <Star size={16} className="text-brand-yellow fill-current" />
                        <span className="font-bold text-gray-600">Nota: {score}/4</span>
                    </div>
                </>
            ) : (
                <p className="text-gray-400 text-sm">{description}</p>
            )}
        </motion.div>
    );
};

const Achievements = () => {
    const { user, progress } = useAuth();

    // Safety check if user somehow gets here without login
    if (!user) return null;

    const userProgress = progress[user.name] || {};

    // Unit Definitions (Scalable list)
    const achievementsList = [
        {
            id: 'unit1', // Corresponds to Unit 5 in UI
            title: 'Maestro de Líneas',
            description: 'Completa la Unidad 1 sobre tipos de líneas.',
        }
    ];

    const launchConfetti = () => {
        confetti({
            particleCount: 100,
            spread: 70,
            origin: { y: 0.6 }
        });
    };

    return (
        <div className="p-4 md:p-8 max-w-6xl mx-auto">
            <header className="mb-12 text-center">
                <div className="inline-block p-2 px-6 bg-brand-yellow/10 rounded-full mb-4">
                    <span className="text-brand-orange font-bold uppercase tracking-widest text-sm">Tu Sala de Trofeos</span>
                </div>
                <h1 className="text-4xl md:text-5xl font-black text-brand-purple mb-4">
                    ¡Tus Logros, {user.name}! 🏆
                </h1>
                <p className="text-xl text-gray-500">Colecciona todas las medallas aprendiendo geometría.</p>
            </header>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {achievementsList.map((ach, index) => {
                    const data = userProgress[ach.id];
                    const unlocked = !!data;

                    return (
                        <Medal
                            key={ach.id}
                            title={ach.title}
                            description={ach.description}
                            unlocked={unlocked}
                            score={data?.score}
                            delay={index * 0.1}
                        />
                    );
                })}
            </div>

            {/* Total Score Summary */}
            <div className="mt-12 bg-white rounded-3xl p-8 shadow-sm border-2 border-gray-100 flex flex-col md:flex-row items-center justify-between gap-6">
                <div>
                    <h3 className="text-2xl font-bold text-gray-800">Resumen Total</h3>
                    <p className="text-gray-500">Sigue así para desbloquear más sorpresas.</p>
                </div>
                <div className="flex gap-8 text-center">
                    <div>
                        <div className="text-4xl font-black text-brand-blue">{Object.keys(userProgress).length}</div>
                        <div className="text-xs font-bold text-gray-400 uppercase tracking-wide">Medallas</div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Achievements;
